/**
 * Builds stage-aware recommended shortcuts for the compact "/" menu.
 * Saved pins first; recommendations fill remaining slots by session stage.
 */

import { useMemo } from 'react';
import type { PinnedChip } from './evidence-settings-popover';
import type { BrowseFilterCategory, ResolvedShortcut } from './shortcut-marketplace-data';

const PRE_NOTE_CATEGORY_ORDER: BrowseFilterCategory[] = [
  'prepare',
  'look_up',
  'work_up',
  'write',
  'treat',
];
const POST_NOTE_CATEGORY_ORDER: BrowseFilterCategory[] = [
  'write',
  'look_up',
  'treat',
  'work_up',
  'prepare',
];

const categoryRank = (
  category: BrowseFilterCategory | undefined,
  order: BrowseFilterCategory[]
): number => {
  if (!category) return order.length + 1;
  const idx = order.indexOf(category);
  return idx === -1 ? order.length : idx;
};

export interface RecommendedShortcutsResult {
  savedShortcuts: PinnedChip[];
  recommendedShortcuts: ResolvedShortcut[];
}

interface UseRecommendedShortcutsArgs {
  pinnedChips: PinnedChip[];
  marketShortcuts: ResolvedShortcut[];
  isPreNoteSession: boolean;
}

export const useRecommendedShortcuts = ({
  pinnedChips,
  marketShortcuts,
  isPreNoteSession,
}: UseRecommendedShortcutsArgs): RecommendedShortcutsResult =>
  useMemo(() => {
    const savedIds = new Set(pinnedChips.map((c) => c.id));
    const savedLabels = new Set(pinnedChips.map((c) => c.label));
    const order = isPreNoteSession ? PRE_NOTE_CATEGORY_ORDER : POST_NOTE_CATEGORY_ORDER;

    const seenIds = new Set<string>();
    const candidates = marketShortcuts.filter((s) => {
      if (savedIds.has(s.id) || savedLabels.has(s.label)) return false;
      if (seenIds.has(s.id)) return false;
      seenIds.add(s.id);
      return true;
    });

    const recommendedShortcuts = candidates
      .map((shortcut, index) => ({ shortcut, index }))
      .sort((a, b) => {
        const primary =
          categoryRank(a.shortcut.browseCategory, order) -
          categoryRank(b.shortcut.browseCategory, order);
        if (primary !== 0) return primary;
        return a.index - b.index;
      })
      .map(({ shortcut }) => shortcut);

    return {
      savedShortcuts: pinnedChips,
      recommendedShortcuts,
    };
  }, [pinnedChips, marketShortcuts, isPreNoteSession]);
